import { ref } from 'vue'
import { htmlToPlainText, plainTextToHtml } from '@/utils/htmlText'

/**
 * Shared plain-text editing for `html`-flagged content fields (the D1/D2 contract):
 * the owner types plain paragraphs while the store stays HTML, so renderers that
 * `v-html` the value are unchanged. Used by both PropertyInspector (component props)
 * and ArrayItemEditor (array-item fields).
 *
 * `lastEmitted` records the HTML WE last wrote, so `seed()` re-derives a draft only on
 * an EXTERNAL change (a different component/item bound to this editor), never from our
 * own keystroke — which is what prevents the cursor jumping when the parent rebuilds
 * the source object on every input.
 */
export function useHtmlDraft() {
  const htmlDrafts = ref<Record<string, string>>({})
  const lastEmitted = ref<Record<string, string>>({})

  /** Re-seed drafts from the currently-bound source. `entries` = [fieldName, currentHtml]. */
  function seed(entries: Array<[string, string]>): void {
    for (const [name, html] of entries) {
      if (html !== lastEmitted.value[name]) htmlDrafts.value[name] = htmlToPlainText(html)
    }
  }

  /** Handle plain-text input: keep the draft as typed, emit the re-wrapped HTML. */
  function write(name: string, plain: string, emit: (html: string) => void): void {
    htmlDrafts.value[name] = plain
    const html = plainTextToHtml(plain)
    lastEmitted.value[name] = html
    emit(html)
  }

  return { htmlDrafts, seed, write }
}
